// Mind maps in the web app (design-docs/Maps — design.md, phase 2): which maps a project has (in Research & Notes, out
// of the Trash), the one in use in this browser (localStorage `ws:map:<project>`, the Mac's UserDefaults
// `map-<project>`), edits through the store's `editMap` (refused under lock), New Mind Map, and the canvas's undo
// (snapshots of the map before each edit, put back whole with `replaceMapContent`). The Plan tab's header and the
// canvas share the zoom and the zoom commands here, as the corkboard's controls share its layout.
import { useSyncExternalStore } from 'react';
import { INK_COLORS, Maps, sameMapContent, scappleImport, scappleSummary, type InkColor, type MapContent, type MapDocument, type Project, type TreeNode } from '@writing-suite/core';
import type { LiveProject } from './project.ts';
import { planEdit, useSharedPreference, writePreference } from './plan.ts';

/** A map in the project, where its contents put it. */
export interface MapEntry {
  fragmentId: string;
  nodeId: string;
  arrangementId: string;
  title: string;
}

/** The project's maps in Research & Notes (every reference arrangement, in reading order); none in the Trash. */
export function mapsOf(project: Project): MapEntry[] {
  const out: MapEntry[] = [];
  const seen = new Set<string>();
  for (const a of project.arrangements()) {
    if (a.role !== 'reference') continue;
    const walk = (nodes: TreeNode[]) => {
      for (const n of nodes) {
        const f = n.fragmentId ? project.fragment(n.fragmentId) : null;
        if (f && f.kind === 'map' && !f.trashed && !seen.has(f.id)) {
          seen.add(f.id);
          out.push({ fragmentId: f.id, nodeId: n.id, arrangementId: a.id, title: n.title || f.title || 'Untitled' });
        }
        walk(n.children);
      }
    };
    walk(project.tree(a.id));
  }
  return out;
}

/** The map in use: the one remembered, else the first. */
export function mapInUse(maps: MapEntry[], remembered: string | null): MapEntry | null {
  return maps.find((m) => m.fragmentId === remembered) ?? maps[0] ?? null;
}

export const MAP_PREF = {
  /** The map in use on Plan ▸ Mind Maps (localStorage `ws:map:<project>`). */
  inUse: (project: string) => `map:${project}`,
  /** The pen: drawing on the paper with a mouse or a finger (a pen always draws), in this browser (the Apple apps'
   *  `mapPenOn`). */
  penOn: 'mapPenOn',
  /** The pen's ink, null = charcoal (the Apple apps' `mapPenInk`). */
  penInk: 'mapPenInk',
};

/** The pen and its ink, shared by the tab's header and the canvas. */
export function useMapPen(): { on: boolean; setOn: (on: boolean) => void; ink: InkColor | null; setInk: (ink: InkColor | null) => void } {
  const [on, setOn] = useSharedPreference<boolean>(MAP_PREF.penOn, false);
  const [stored, setInk] = useSharedPreference<InkColor | null>(MAP_PREF.penInk, null);
  const ink = typeof stored === 'string' && (INK_COLORS as readonly string[]).includes(stored) ? stored : null;
  return { on: on === true, setOn, ink, setInk };
}

/** A change to a map through the store (refused under lock; lines left to a deleted note tidied). */
export function mapEdit<T>(live: Pick<LiveProject, 'edit'>, fragmentId: string, fn: (map: MapDocument) => T): T {
  return planEdit(live, (e) => e.editMap(fragmentId, fn));
}

/** New Mind Map: named after the maps there are ("Mind Map", "Mind Map 2"…), in the Mind Maps folder, with the project's
 *  title as its centre. The canvas opens it with that note ready to type over. */
export function newMindMap(live: Pick<LiveProject, 'edit'>, project: Project): { fragmentID: string; nodeID: string } {
  const title = Maps.newTitle(mapsOf(project).map((m) => m.title));
  const made = planEdit(live, (e) => e.newMap({ title }));
  editCentreNext.add(made.fragmentID);
  return made;
}

// MARK: Import Scapple Board…

/** A board's name as the map's: the file's, without `.scap`. */
export const scappleTitle = (fileName: string) => fileName.replace(/\.scap$/i, '').trim() || 'Scapple Board';

/** Import Scapple Board… (`ScappleImporter`; the Mac's File ▸ Import ▸ Scapple Board…): the board becomes a new map in
 *  Mind Maps named after the file, and the map in use here. The canvas opens it fitted, says what came in (the report's
 *  summary), and offers Link Notes to Cast… when notes name someone in the cast. */
export function importScappleBoard(live: Pick<LiveProject, 'edit'>, projectId: string, fileName: string, bytes: Uint8Array): { fragmentID: string; summary: string } {
  let read: ReturnType<typeof scappleImport>;
  try {
    read = scappleImport(bytes);
  } catch {
    throw new Error(`“${fileName}” isn’t a Scapple board.`);
  }
  const made = planEdit(live, (e) => e.newMap({ title: scappleTitle(fileName), content: read.content }));
  const summary = scappleSummary(read.report);
  importedNext.set(made.fragmentID, summary);
  writePreference(MAP_PREF.inUse(projectId), made.fragmentID);
  return { fragmentID: made.fragmentID, summary };
}

const importedNext = new Map<string, string>();

/** What an import brought, for the canvas to say once (and then offer the cast); null for a map not just imported. */
export function takeImported(fragmentId: string): string | null {
  const summary = importedNext.get(fragmentId) ?? null;
  importedNext.delete(fragmentId);
  return summary;
}

// MARK: A new map opens with its centre being typed over

const editCentreNext = new Set<string>();

/** Whether the canvas should open this map with its centre selected and its words selected (once). */
export function takeCentreEdit(fragmentId: string): boolean {
  return editCentreNext.delete(fragmentId);
}

// MARK: Undo

/** The canvas's undo: the map's content before each edit. A `group` keeps one step for several edits (a note made and
 *  then typed in, a drag), the first edit's state being the one undo returns to. */
export class MapUndo<T = MapContent> {
  private undos: T[] = [];
  private redos: T[] = [];
  private group: string | null = null;

  constructor(private readonly limit = 200, private readonly same: (a: T, b: T) => boolean = sameMapContent as unknown as (a: T, b: T) => boolean) {}

  /** Before an edit: the state to return to. Nothing is kept for an edit that changed nothing (`after` the same). */
  record(before: T, after: T, group: string | null = null) {
    if (this.same(before, after)) return;
    if (group !== null && group === this.group) return;
    this.undos.push(before);
    if (this.undos.length > this.limit) this.undos.shift();
    this.redos = [];
    this.group = group;
  }

  /** The last step taken back when it left the map as it was (a new note emptied and removed). */
  dropIfSame(current: T) {
    const last = this.undos.at(-1);
    if (last !== undefined && this.same(last, current)) {
      this.undos.pop();
      this.group = null;
    }
  }

  /** Ends a group: the next edit is a step of its own. */
  close() {
    this.group = null;
  }

  undo(current: T): T | null {
    const state = this.undos.pop();
    if (state === undefined) return null;
    this.redos.push(current);
    this.group = null;
    return state;
  }

  redo(current: T): T | null {
    const state = this.redos.pop();
    if (state === undefined) return null;
    this.undos.push(current);
    this.group = null;
    return state;
  }

  get canUndo() {
    return this.undos.length > 0;
  }

  get canRedo() {
    return this.redos.length > 0;
  }
}

// MARK: Zoom, shared by the tab's header and the canvas

/** The zoom, and the map menu's Add to Map…, Link Notes to Cast…, Arrange Around Centre and Export as PNG…/PDF…, which the
 *  canvas carries out. */
export type MapCommand = 'in' | 'out' | 'fit' | 'actual' | 'add' | 'cast' | 'arrange' | 'png' | 'pdf';

const zooms = new Map<string, number>();
const zoomListeners = new Set<() => void>();
const commandListeners = new Map<string, Set<(c: MapCommand) => void>>();

/** The canvas's zoom now (1 = 100%). */
export function publishMapZoom(fragmentId: string, zoom: number) {
  if (zooms.get(fragmentId) === zoom) return;
  zooms.set(fragmentId, zoom);
  for (const fn of zoomListeners) fn();
}

export function useMapZoom(fragmentId: string | null): number {
  return useSyncExternalStore(
    (fn) => {
      zoomListeners.add(fn);
      return () => zoomListeners.delete(fn);
    },
    () => (fragmentId ? (zooms.get(fragmentId) ?? 1) : 1),
    () => 1,
  );
}

/** −, +, Fit and Actual Size from outside the canvas (the header's buttons). */
export function sendMapCommand(fragmentId: string, command: MapCommand) {
  for (const fn of commandListeners.get(fragmentId) ?? []) fn(command);
}

export function onMapCommand(fragmentId: string, fn: (c: MapCommand) => void): () => void {
  const set = commandListeners.get(fragmentId) ?? new Set();
  set.add(fn);
  commandListeners.set(fragmentId, set);
  return () => set.delete(fn);
}
